import { HttpError } from 'react-admin';
import type { DataProvider, GetListParams, GetOneParams, CreateParams, UpdateParams, DeleteParams, DeleteManyParams } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchPricingRules } from './fetchPricingRules';
import { fetchPricingRule } from './fetchPricingRule';
import { createPricingRule } from './createPricingRule';
import { updatePricingRule } from './updatePricingRule';
import { deletePricingRule } from './deletePricingRule';
import { batchDeletePricingRules } from './batchDeletePricingRules';
import { updatePricingRulePosition } from './updatePricingRulePosition';
import { startPricingImport } from './startPricingImport';
import { addToPricingImport } from './addToPricingImport';
import { initiatePricing } from './initiatePricing';
import { fetchPricingHistory } from './fetchPricingHistory';
import { generateResalePricingExport } from './generateResalePricingExport';
import type {
  PricingRule,
  PricingFilters,
  PricingRuleInput,
  PricingRuleUpdateInput,
  PageInfo,
  StartPricingImportResult,
  PricingHistoryEntry,
  PricingExportResult,
} from './types';

const PRICING_TYPE = 'condition' as const;

/**
 * Internal interface with proper types for pricing rules data provider.
 * React-admin's DataProvider is generic which prevents type-safe single-resource providers.
 */
interface ConditionDiscountsDataProviderImpl {
  getList: (
    resource: string,
    params: GetListParams,
  ) => Promise<{
    data: PricingRule[];
    total: number | undefined;
    pageInfo: PageInfo;
    meta: { initiatedAt: string | undefined, processedAt: string | undefined };
  }>;
  getOne: (
    resource: string,
    params: GetOneParams,
  ) => Promise<{ data: PricingRule }>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: (
    resource: string,
    params: CreateParams,
  ) => Promise<{ data: PricingRule }>;
  update: (
    resource: string,
    params: UpdateParams,
  ) => Promise<{ data: PricingRule }>;
  updateMany: () => Promise<never>;
  delete: (
    resource: string,
    params: DeleteParams,
  ) => Promise<{ data: { id: number } }>;
  deleteMany: (
    resource: string,
    params: DeleteManyParams,
  ) => Promise<{ data: (string | number)[] }>;
  startImport: (
    resource: string,
    params: Record<string, never>,
  ) => Promise<{ data: StartPricingImportResult }>;
  addToImport: (
    resource: string,
    params: { pricingDiscountId: number, data: PricingRuleInput[] },
  ) => Promise<{ created: number, errors: { index: number, message: string }[] }>;
  updatePosition: (
    resource: string,
    params: { id: number, newPosition: number },
  ) => Promise<{ data: { id: number, position: number } }>;
  initiate: (
    resource: string,
    params: Record<string, never>,
  ) => Promise<{ data: { initiatedAt: string } }>;
  getHistory: (
    resource: string,
    params: Record<string, never>,
  ) => Promise<{ data: PricingHistoryEntry[] }>;
  generateExport: (
    resource: string,
    params: { id: number },
  ) => Promise<{ data: PricingExportResult }>;
}

/**
 * Extended data provider interface with custom methods.
 *
 * Custom methods (not part of standard react-admin DataProvider):
 * - startImport: Start a pricing import session, returns pricingDiscountId
 * - addToImport: Add rules to an existing import session (processes in batches of 50)
 * - updatePosition: Move a rule to a new position in the list
 * - initiate: Start background job to apply pricing rules to catalog
 * - getHistory: Fetch past snapshots for this pricing type (powers the History view)
 * - generateExport: Request a presigned S3 URL for a snapshot's TSV download
 */
export interface ConditionDiscountsDataProvider extends DataProvider {
  startImport: ConditionDiscountsDataProviderImpl['startImport'];
  addToImport: ConditionDiscountsDataProviderImpl['addToImport'];
  updatePosition: ConditionDiscountsDataProviderImpl['updatePosition'];
  initiate: ConditionDiscountsDataProviderImpl['initiate'];
  getHistory: ConditionDiscountsDataProviderImpl['getHistory'];
  generateExport: ConditionDiscountsDataProviderImpl['generateExport'];
}

const impl: ConditionDiscountsDataProviderImpl = {
  getList: async (resource, params) => {
    try {
      const { page = 1, perPage = 50 } = params.pagination || {};
      const after = page > 1 ?
        btoa(`cursor:${(page - 1) * perPage - 1}`) :
        null;

      // Extract filter values
      const filters: PricingFilters = {};
      if (params.filter) {
        if (params.filter.department) filters.department = params.filter.department;
        if (params.filter.category) filters.category = params.filter.category;
        if (params.filter.tag) {
          filters.tag = params.filter.tag;
          // A facet value is meaningless without its tag; only send it with the
          // tag so a transient tag-less `name` (e.g. mid-removal of the Facet
          // filter) can't fire a query for an orphaned value.
          if (params.filter.name) filters.name = params.filter.name;
        }
        if (params.filter.merchantCategory) filters.merchantCategory = params.filter.merchantCategory;
        if (params.filter.upc) filters.upc = params.filter.upc;
        if (params.filter.condition) filters.condition = params.filter.condition;
      }

      const response = await fetchPricingRules({
        partnerId: requirePartner().id,
        pricingType: PRICING_TYPE,
        first: perPage,
        after,
        filters,
      });

      const data: PricingRule[] = response.edges.map(edge => edge.node);

      return {
        data,
        total: response.totalCount ?? undefined,
        pageInfo: response.pageInfo,
        meta: {
          initiatedAt: response.initiatedAt ?? undefined,
          processedAt: response.processedAt ?? undefined,
        },
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.getList failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load pricing rules.', status);
    }
  },

  getOne: async (resource, params) => {
    try {
      const rule = await fetchPricingRule({
        partnerId: requirePartner().id,
        ruleId: params.id,
      });

      if (!rule) {
        throw new HttpError('Pricing rule not found', 404);
      }

      return { data: rule };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.getOne failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load pricing rule.', status);
    }
  },

  getMany: async () => {
    throw new Error('getMany not implemented for pricing rules resource');
  },

  getManyReference: async () => {
    throw new Error('getManyReference not implemented for pricing rules resource');
  },

  create: async (resource, params) => {
    try {
      const rule = await createPricingRule({
        partnerId: requirePartner().id,
        pricingType: PRICING_TYPE,
        rule: params.data as PricingRuleInput,
      });

      return { data: rule };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.create failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to create pricing rule.', status);
    }
  },

  update: async (resource, params) => {
    try {
      const partner = requirePartner();

      // Extract only the fields that PricingRuleUpdateInput accepts
      const {
        id: _id,
        position: _position,
        name: _name,
        ...rest
      } = params.data;

      const updates: PricingRuleUpdateInput = {};
      if (rest.sourceColumn !== undefined) updates.sourceColumn = rest.sourceColumn;
      if (rest.discount !== undefined) updates.discount = rest.discount;
      if (rest.department !== undefined) updates.department = rest.department || undefined;
      if (rest.category !== undefined) updates.category = rest.category || undefined;
      if (rest.merchantCategory !== undefined) updates.merchantCategory = rest.merchantCategory || undefined;
      if (rest.tag !== undefined) updates.tag = rest.tag || undefined;
      if (_name !== undefined) updates.name = _name || undefined;
      if (rest.secondaryTag !== undefined) updates.secondaryTag = rest.secondaryTag || undefined;
      if (rest.secondaryName !== undefined) updates.secondaryName = rest.secondaryName || undefined;
      if (rest.tertiaryTag !== undefined) updates.tertiaryTag = rest.tertiaryTag || undefined;
      if (rest.tertiaryName !== undefined) updates.tertiaryName = rest.tertiaryName || undefined;
      if (rest.upc !== undefined) updates.upc = rest.upc || undefined;
      if (rest.condition !== undefined) updates.condition = rest.condition || undefined;

      const rule = await updatePricingRule({
        partnerId: partner.id,
        ruleId: Number(params.id),
        updates,
      });

      return { data: rule };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.update failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to update pricing rule.', status);
    }
  },

  updateMany: async () => {
    throw new Error('updateMany not implemented for pricing rules resource');
  },

  delete: async (resource, params) => {
    try {
      await deletePricingRule({
        partnerId: requirePartner().id,
        ruleId: Number(params.id),
      });

      return { data: { id: Number(params.id) } };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.delete failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to delete pricing rule.', status);
    }
  },

  deleteMany: async (resource, params) => {
    try {
      const ruleIds = params.ids.map(id => Number(id));

      await batchDeletePricingRules({
        partnerId: requirePartner().id,
        ruleIds,
      });

      return { data: params.ids };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.deleteMany failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to delete pricing rules.', status);
    }
  },

  /**
   * Start a pricing import session.
   * Creates a new PricingDiscount record and returns its ID.
   */
  startImport: async (resource) => {
    try {
      const result = await startPricingImport({
        partnerId: requirePartner().id,
        pricingType: PRICING_TYPE,
      });

      return { data: result };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.startImport failed:', {
        resource,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to start pricing import.', status);
    }
  },

  /**
   * Add pricing rules to an existing import session.
   * Processes rules in batches of 50.
   */
  addToImport: async (resource, params) => {
    try {
      const { pricingDiscountId, data: rules } = params;

      const partner = requirePartner();

      const BATCH_SIZE = 50;
      let totalCreated = 0;
      const allErrors: { index: number, message: string }[] = [];

      // Process in batches
      for (let i = 0; i < rules.length; i += BATCH_SIZE) {
        const batch = rules.slice(i, i + BATCH_SIZE);
        const result = await addToPricingImport({
          partnerId: partner.id,
          pricingDiscountId,
          rules: batch,
        });

        totalCreated += result.created ?? 0;

        // Adjust error indices to be relative to the full list
        const adjustedErrors = (result.errors ?? [])
          .filter((err): err is NonNullable<typeof err> => err != null)
          .map(err => ({
            index: (err.index ?? 0) + i,
            message: err.message ?? 'Unknown error',
          }));
        allErrors.push(...adjustedErrors);
      }

      return {
        created: totalCreated,
        errors: allErrors,
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.addToImport failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to import pricing rules.', status);
    }
  },

  /**
   * Update a pricing rule's position.
   */
  updatePosition: async (resource, params) => {
    try {
      const result = await updatePricingRulePosition({
        partnerId: requirePartner().id,
        ruleId: params.id,
        newPosition: params.newPosition,
      });

      return {
        data: {
          id: result.id ?? params.id,
          position: result.position ?? params.newPosition,
        },
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.updatePosition failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to update pricing rule position.', status);
    }
  },

  /**
   * Initiate pricing rule processing.
   * Starts a background job that applies pricing rules to catalog items.
   */
  initiate: async (resource) => {
    try {
      const initiatedAt = await initiatePricing({
        partnerId: requirePartner().id,
        pricingType: PRICING_TYPE,
      });

      return { data: { initiatedAt } };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.initiate failed:', {
        resource,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to initiate pricing.', status);
    }
  },

  /**
   * Fetch the list of past pricing-rule snapshots for this resource's pricing type.
   * Powers the History view.
   */
  getHistory: async (resource) => {
    try {
      const entries = await fetchPricingHistory({
        partnerId: requirePartner().id,
        pricingType: PRICING_TYPE,
      });

      return { data: entries };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.getHistory failed:', {
        resource,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load pricing history.', status);
    }
  },

  /**
   * Request a presigned URL for downloading a snapshot's TSV.
   * Caller (component) opens the URL in a new tab to trigger the download.
   */
  generateExport: async (resource, params) => {
    try {
      const result = await generateResalePricingExport({
        partnerId: requirePartner().id,
        id: params.id,
      });

      return { data: result };
    } catch (error) {
      // Snapshot-not-found is the only user-actionable subcode this mutation
      // is known to emit (the history row points at a deleted snapshot).
      // Surface a specific message rather than the generic one so the user can
      // act on it and support routes correctly. Any other subcode falls through
      // to the generic 500 wrap below — revisit if reware-api adds another
      // user-actionable code.
      //
      // Reware-api's errorFormatter returns `subcode` at the root of the GraphQL
      // error object (NOT under `extensions`); graphql-http forwards that flat
      // object into `errors[0]` verbatim, and `execAdmin` attaches it as
      // `HttpError.body`. So the access path is `body.subcode`, not
      // `body.extensions.subcode`.
      if (
        error instanceof HttpError &&
        (error as { body?: { subcode?: string } }).body?.subcode === 'DATA_NOT_FOUND'
      ) {
        throw new HttpError('This pricing snapshot is no longer available.', 404);
      }
      if (error instanceof HttpError) throw error;
      console.error('conditionDiscountsDataProvider.generateExport failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to generate pricing export.', status);
    }
  },
};

// Cast to ConditionDiscountsDataProvider for react-admin compatibility.
// React-admin's DataProvider methods are generic, which prevents type-safe
// single-resource providers. We define proper types above and cast here.
export const conditionDiscountsDataProvider = impl as unknown as ConditionDiscountsDataProvider;
